import React from "react";
import { Box, Chip, Grid, Typography } from "@mui/material";
import { Modal } from "../Modal";
import { bagStatusLabel, hasBagsLoadedData, parseDateTime } from "../../helpers";
import { useDataServiceContext } from "../../hooks";
import {
  formatPassengerRewardDisplay,
  formatRewardDisplay,
  isPriorityServiceCode,
  isRewardServiceCode,
  parseRewardCode,
} from "../../helpers/rewardTier";
import { categoryByKey, categoryKeyForCode } from "../../helpers/ssrCategories";
import { ReactComponent as AvatarBoardedIcon } from "../../images/avatarBoarded.svg";
import { ReactComponent as AvatarCheckedInIcon } from "../../images/avatarCheckedIn.svg";
import { ReactComponent as AvatarNotCheckedInIcon } from "../../images/avatarNotCheckedIn.svg";

const getConnectionLabel = (flight, type) => {
  if (!flight) return "-";

  let scheduledTime = null;
  let estimatedTime = null;
  let actualTime = null;
  let dateStr = "-";

  if (type === "Outbound") {
    scheduledTime = flight.departureTime
      ? parseDateTime(flight.departureTime)
      : null;
    estimatedTime = flight.estimatedDepartureTime
      ? parseDateTime(flight.estimatedDepartureTime)
      : null;
    actualTime = flight.actualDepartureTime
      ? parseDateTime(flight.actualDepartureTime)
      : null;
    dateStr = scheduledTime ? scheduledTime.date : "-";
  } else if (type === "Inbound") {
    scheduledTime = flight.arrivalTime
      ? parseDateTime(flight.arrivalTime)
      : null;
    estimatedTime = flight.estimatedArrivalTime
      ? parseDateTime(flight.estimatedArrivalTime)
      : null;
    actualTime = flight.actualArrivalTime
      ? parseDateTime(flight.actualArrivalTime)
      : null;
    dateStr = scheduledTime ? scheduledTime.date : "-";
  }

  const times = [];

  // Check if we have a departureTimeQualifier for outbound flights
  if (type === "Outbound" && flight.departureTimeQualifier) {
    // If actual departure time is available, use that
    if (flight.departureTimeQualifier === "ATD" && scheduledTime) {
      times.push(`ATD ${scheduledTime.time}`);
    } else if (flight.departureTimeQualifier === "ETD" && scheduledTime) {
      times.push(`ETD ${scheduledTime.time}`);
    } else if (flight.departureTimeQualifier === "STD" && scheduledTime) {
      times.push(`STD ${scheduledTime.time}`);
    } else if (scheduledTime) {
      // Fall back to the scheduled time with proper qualifier
      times.push(`${flight.departureTimeQualifier} ${scheduledTime.time}`);
    }
  }
  // Check if we have an arrivalTimeQualifier for inbound flights
  else if (type === "Inbound" && flight.arrivalTimeQualifier) {
    times.push(`${flight.arrivalTimeQualifier} ${scheduledTime.time}`);
  }
  // Otherwise fall back to the old behavior
  else if (scheduledTime) {
    const scheduledIndicator = type === "Outbound" ? "STD" : "STA";
    times.push(`${scheduledIndicator} ${scheduledTime.time}`);
  }

  // Only add ETD if we don't already have ATD
  if (
    estimatedTime &&
    !(type === "Outbound" && flight.departureTimeQualifier === "ATD") &&
    !(type === "Outbound" && flight.departureTimeQualifier === "ETD") &&
    !(type === "Inbound" && flight.arrivalTimeQualifier === "ATA") &&
    !(type === "Inbound" && flight.arrivalTimeQualifier === "ETA")
  ) {
    const estimatedIndicator = type === "Outbound" ? "ETD" : "ETA";
    times.push(`${estimatedIndicator} ${estimatedTime.time}`);
  }

  // Only add ATD if we don't already have it from the qualifier
  if (
    actualTime &&
    !(type === "Outbound" && flight.departureTimeQualifier === "ATD") &&
    !(type === "Inbound" && flight.arrivalTimeQualifier === "ATA")
  ) {
    const actualIndicator = type === "Outbound" ? "ATD" : "ATA";
    times.push(`${actualIndicator} ${actualTime.time}`);
  }

  let depDestString = "";
  if (type === "Outbound") {
    depDestString = `${flight.departure}-${flight.destination}`;
  } else {
    depDestString = `${flight.departure}-${flight.destination}`;
  }

  let locationInfo = "";
  if (type === "Inbound") {
    locationInfo = flight.arrivalStand ? ` ${flight.arrivalStand}` : "";
  } else {
    locationInfo = flight.departureGate
      ? ` ${flight.departureGate}`
      : flight.gate
      ? ` ${flight.gate}`
      : "";
  }

  return `${flight.flightNumber} / ${dateStr} ${depDestString} ${times.join(
    " "
  )}${locationInfo}`;
};

export const PassengerModal = ({
  selectedPassenger,
  isOpen,
  handleClose,
  bagInfo = null,
}) => {
  const { flightData } = useDataServiceContext();
  const showLoadedStatus = hasBagsLoadedData(flightData?.bagList);
  const {
    id,
    name,
    mealObject,
    seat,
    boarded,
    checkedIn,
    passengerType,
    wheelchair,
    services = "",
    inboundConnections = [],
    outboundConnections = [],
    customerBookingReference,
    securityNumber,
    isStaff,
    frequentFlyerTierLevel,
  } = selectedPassenger;

  const rewardDisplay = formatPassengerRewardDisplay(selectedPassenger);

  const boardedLabel = id
    ? boarded
      ? "Boarded"
      : checkedIn
      ? "Checked in"
      : "Not checked in"
    : "Vacant";
  const specialNeedsLabel = wheelchair ? "WCHR" : "-";

  const seatLabel = seat?.replace(/^0+(\d+\w)$/gm, "$1") || "-";
  const mealCode = mealObject?.code || "";
  const mealCategory = mealObject?.category || null;
  const mealDescription = mealObject?.description || "";
  const passengerTypeLabel =
    (passengerType === "C"
      ? "Child"
      : passengerType === "A"
      ? "Adult"
      : passengerType || "-") + (isStaff ? " - ID STAFF" : "");

  const hasBagData =
    bagInfo &&
    ((bagInfo.checkedInCount ?? 0) > 0 ||
      (bagInfo.loadedCount ?? 0) > 0 ||
      (Array.isArray(bagInfo.bags) && bagInfo.bags.length > 0));

  const bagCountSummary = showLoadedStatus
    ? `Checked-in: ${bagInfo?.checkedInCount ?? 0} | Loaded: ${
        bagInfo?.loadedCount ?? 0
      }`
    : `Checked-in: ${bagInfo?.checkedInCount ?? 0}`;

  const renderServicesList = (servicesArray) => {
    if (!Array.isArray(servicesArray) || servicesArray.length === 0) {
      return (
        <Typography variant="body2" component="span">
          -
        </Typography>
      );
    }

    const filteredServices = servicesArray.filter((service) => {
      const code = service?.code;
      if (!code) return false;
      if (isPriorityServiceCode(code)) return false;
      if (frequentFlyerTierLevel && code === frequentFlyerTierLevel) return false;
      return !isRewardServiceCode(code);
    });

    if (filteredServices.length === 0) {
      return (
        <Typography variant="body2" component="span">
          -
        </Typography>
      );
    }

    return (
      <Box component="ul" sx={{ paddingLeft: 2, margin: 0 }}>
        {filteredServices.map((service, id) => {
          const code = service?.code;
          const description = service?.description || "";
          const text = service?.text || "";
          const rewardFromCode = parseRewardCode(code);
          const displayCode = rewardFromCode
            ? formatRewardDisplay(rewardFromCode)
            : code || "";
          const category = categoryByKey(categoryKeyForCode(code));
          return (
            <li key={`svc-${id}`}>
              {category ? (
                <Box
                  component="span"
                  title={category.label}
                  sx={{ display: "inline-flex" }}
                >
                  <Chip label={displayCode} size="small" sx={{ height: 20 }} />
                </Box>
              ) : (
              <Typography variant="body2" component="div">
                {displayCode}
                {!rewardFromCode && description ? ` - ${description}` : ""}
              </Typography>
              )}
              {category && description ? (
                <Typography variant="caption" component="div">
                  {description}
                </Typography>
              ) : null}
              {text ? (
                <Typography variant="caption" component="div">
                  {text}
                </Typography>
              ) : null}
            </li>
          );
        })}
      </Box>
    );
  };

  const AvatarIcon = boarded
    ? AvatarBoardedIcon
    : checkedIn
    ? AvatarCheckedInIcon
    : AvatarNotCheckedInIcon;


  return (
    <Modal isOpen={isOpen} handleClose={handleClose}>
      <div className="map-modal-container" style={{ width: 300 }}>
        <Grid item>
          <AvatarIcon style={{ width: 64, height: 64 }} />
        </Grid>
        <Grid item>
          <Typography variant="h6" textAlign={"center"}>
            {name}
          </Typography>
          <Typography textAlign={"center"} variant="subtitle2">
            PNR: {customerBookingReference}
          </Typography>
        </Grid>
        <Grid container direction={"column"} spacing={2}>
          <Grid item>
            <Grid
              container
              style={{
                display: "flex",
                flexFlow: "row wrap",
                justifyContent: "space-between",
                columnGap: 8,
                width: "100%",
              }}
            >
              <Grid item>
              <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontWeight: "bold" }}
              >
                  {"Status: "}
              </Typography>
                <Typography variant="body2" component="span">
                {boardedLabel}
              </Typography>
              </Grid>
              <Grid item>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontWeight: "bold" }}
                >
                  {"Seq. Nr: "}
                </Typography>
                <Typography variant="body2" component="span">
                  {securityNumber}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Box>
              <Typography
                variant="body2"
                component="span"
                sx={{ fontWeight: "bold" }}
              >
                {"Type: "}
              </Typography>
              <Typography variant="body2" component="span">
                {passengerTypeLabel}
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box>
              <Typography
                variant="body2"
                component="span"
                sx={{ fontWeight: "bold" }}
              >
                {"Seat: "}
              </Typography>
              <Typography variant="body2" component="span">
                {seatLabel}
              </Typography>
            </Box>
          </Grid>
        {hasBagData && (
          <Grid item>
            <Box>
              <Typography
                variant="body2"
                component="div"
                sx={{ fontWeight: "bold" }}
              >
                {"Bags: "}
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontWeight: "normal" }}
                >
                  {`${bagCountSummary}${
                    bagInfo?.source === "pnr" ? " (PNR total)" : ""
                  }`}
                </Typography>
              </Typography>
              {Array.isArray(bagInfo?.bags) && bagInfo.bags.length > 0 && (
                <Box component="ul" sx={{ paddingLeft: 2, margin: 0, mt: 1 }}>
                  {bagInfo.bags.map((bag, idx) => (
                    <li
                      key={`bag-${bag?.id || bag?.tagNumber || bag?.licensePlate || idx}`}
                    >
                      <Typography variant="body2" component="div">
                        Tag: {bag?.tagNumber || bag?.licensePlate || "-"}
                      </Typography>
                      <Typography variant="caption" component="div">
                        Status: {bagStatusLabel(bag, showLoadedStatus)}
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Box>
          </Grid>
        )}
          <Grid item>
            <Box>
              <Typography
                variant="body2"
                component="div"
                sx={{ fontWeight: "bold" }}
              >
                {"Meal: "}
              <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontWeight: "normal" }}
              >
                  {mealCode
                    ? mealCategory
                      ? mealCategory === "Special"
                        ? `${mealCategory} ${mealCode}`
                        : mealCategory
                      : mealCode
                    : "-"}
                </Typography>
              </Typography>
              {mealCode && mealDescription ? (
                <Typography variant="caption" component="div">
                  {mealDescription}
              </Typography>
              ) : null}
            </Box>
          </Grid>
          <Grid item>
            <Box>
              <Typography
                variant="body2"
                component="span"
                sx={{ fontWeight: "bold" }}
              >
                {"Reward: "}
              </Typography>
              <Typography variant="body2" component="span">
                {rewardDisplay || "-"}
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box>
              <Typography
                variant="body2"
                component="span"
                sx={{ fontWeight: "bold" }}
              >
                {"Services: "}
              </Typography>
              {renderServicesList(services)}
            </Box>
          </Grid>
          <Grid item>
            <Box>
              <Typography
                variant="body2"
                component="span"
                sx={{ fontWeight: "bold" }}
              >
                {"Connections:"}
              </Typography>
              <br />
              {inboundConnections.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                    INBOUND:
                  </Typography>
                  {inboundConnections.map((flight, idx) => (
                  <Grid item key={`inbound-${idx}`}>
                      <Typography variant="caption" component="span">
                        {getConnectionLabel(flight, "Inbound")}
                    </Typography>
                  </Grid>
                ))}
                </>
              )}
              {outboundConnections.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                    OUTBOUND:
                  </Typography>
                  {outboundConnections.map((flight, idx) => (
                  <Grid item key={`outbound-${idx}`}>
                      <Typography variant="caption" component="span">
                        {getConnectionLabel(flight, "Outbound")}
              </Typography>
                  </Grid>
                ))}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </div>
    </Modal>
  );
};
